import { ApiError } from '@/api/ApiError'
import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { CopyableValue } from '@/components/CopyableValue/CopyableValue'
import { DisabledReasonTooltip } from '@/components/DisabledReasonTooltip/DisabledReasonTooltip'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import SkeletonTable from '@/components/SkeletonTable'
import { useRBACPermissions } from '@/contexts/useRBACPermissions'
import { useCanCreateAccount } from '@/features/AccountManagement/hooks/useCanCreateAccount'
import { routeToAccountManagementWithPrefilledEmail } from '@/features/AccountManagement/pages/AccountManagement'
import { getAuth0UserLink } from '@/features/HivesOverview/components/ExternalLinks'
import { MemberRoleBadge } from '@/features/HivesOverview/components/MemberRoleBadge'
import { AddMembersDialog } from '@/features/OrgMembers/components/AddMembersDialog'
import { invalidateOrgUsers } from '@/features/OrgMembers/lib/invalidateOrgUsers'
import {
  type ChangeOrgOwnerVariables,
  useChangeOrgOwner,
} from '@/generated/http-clients/v2ApiComponents'
import {
  useAddSupportUser,
  useGetOrgRoles,
  useGetOrgUsers,
  useGetOrganisationFeatures,
  useRemoveSupportUser,
  useReplaceUserRoles,
} from '@/generated/http-clients/v3ApiComponents'
import type { OrgUser } from '@/generated/http-clients/v3ApiSchemas'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  showOrUpdateToast,
} from '@/hooks/useToast/toastUtils'
import {
  Button,
  CloseButton,
  Dialog,
  Field,
  HStack,
  Icon,
  IconButton,
  Input,
  List,
  NativeSelect,
  Table,
  Text,
} from '@chakra-ui/react'
import { type UseQueryResult, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import get from 'lodash/get'
import { Crown, Pencil, Plus, Trash2, UserPlus } from 'lucide-react'
import type { FormEvent, PropsWithChildren, ReactNode } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type MembersCardProps = Readonly<{
  orgId: string
  canManageSupportUsers: boolean
  canEditRoles: boolean
  canonicalOrgId?: string
  addMembersRequested: boolean
  onAddMembersRequestHandled: () => void
  ownerId?: string
}>

const ADD_SUPPORT_USER_TOAST_ID = 'add-support-user-toast'
const REMOVE_SUPPORT_USER_TOAST_ID = 'remove-support-user-toast'
const EDIT_ROLE_TOAST_ID = 'edit-user-role-toast'
const MAKE_OWNER_TOAST_ID = 'make-owner-toast'
const SUPPORT_ADMIN_ROLE = 'support_admin'

type MakeOwnerFailure = Readonly<{ errorKey: string; closesDialog: boolean }>

const MAKE_OWNER_FAILURES: Readonly<Record<number, MakeOwnerFailure>> = {
  400: {
    errorKey: 'orgDetails.members.makeOwnerDialog.errors.badRequest',
    closesDialog: false,
  },
  404: {
    errorKey: 'orgDetails.members.makeOwnerDialog.errors.notFound',
    closesDialog: true,
  },
  409: {
    errorKey: 'orgDetails.members.makeOwnerDialog.errors.conflict',
    closesDialog: true,
  },
  500: {
    errorKey: 'orgDetails.members.makeOwnerDialog.errors.partial',
    closesDialog: true,
  },
  503: {
    errorKey: 'orgDetails.members.makeOwnerDialog.errors.unavailable',
    closesDialog: false,
  },
}

function makeOwnerFailure(error: unknown): MakeOwnerFailure | undefined {
  return error instanceof ApiError
    ? MAKE_OWNER_FAILURES[error.status]
    : undefined
}

function badRequestMessage(error: unknown): string | undefined {
  if (!(error instanceof ApiError) || error.status !== 400) {
    return undefined
  }
  const message = get(error.body, 'message')
  return typeof message === 'string' && message.length > 0 ? message : undefined
}

type FormDialogProps = Readonly<
  PropsWithChildren<{
    open: boolean
    onClose: () => void
    onSubmit: (event: FormEvent<HTMLFormElement>) => void
    title: ReactNode
    cancelLabel: ReactNode
    submitLabel: ReactNode
    submitColorPalette: 'brand' | 'red'
    submitLoading: boolean
    submitDisabled: boolean
  }>
>

function FormDialog({
  open,
  onClose,
  onSubmit,
  title,
  cancelLabel,
  submitLabel,
  submitColorPalette,
  submitLoading,
  submitDisabled,
  children,
}: FormDialogProps) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(details) => {
        if (!details.open) {
          onClose()
        }
      }}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content as="form" onSubmit={onSubmit}>
          <Dialog.Header pb={3}>
            <Dialog.Title>{title}</Dialog.Title>
            <Dialog.CloseTrigger asChild>
              <CloseButton />
            </Dialog.CloseTrigger>
          </Dialog.Header>
          <Dialog.Body>{children}</Dialog.Body>
          <Dialog.Footer>
            <HStack gap={3}>
              <Button variant="outline" onClick={onClose}>
                {cancelLabel}
              </Button>
              <Button
                type="submit"
                colorPalette={submitColorPalette}
                loading={submitLoading}
                disabled={submitDisabled}
              >
                {submitLabel}
              </Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  )
}

type ReasonFieldProps = Readonly<{
  label: ReactNode
  placeholder: string
  helper: ReactNode
  value: string
  onChange: (value: string) => void
}>

function ReasonField({
  label,
  placeholder,
  helper,
  value,
  onChange,
}: ReasonFieldProps) {
  return (
    <Field.Root required>
      <Field.Label>{label}</Field.Label>
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        size="lg"
        borderColor="surface.500"
        borderWidth="1px"
      />
      <Field.HelperText>{helper}</Field.HelperText>
    </Field.Root>
  )
}

export function MembersCard({
  orgId,
  canManageSupportUsers,
  canEditRoles,
  canonicalOrgId,
  addMembersRequested,
  onAddMembersRequestHandled,
  ownerId,
}: MembersCardProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t: tCommon } = useTranslation(undefined, { useSuspense: false })
  const { t } = useTranslation('features', { useSuspense: false })

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false)
  const [isEditRoleDialogOpen, setIsEditRoleDialogOpen] = useState(false)
  const [isMakeOwnerDialogOpen, setIsMakeOwnerDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<OrgUser | null>(null)
  const [reason, setReason] = useState('')
  const [makeOwnerTarget, setMakeOwnerTarget] = useState<OrgUser | null>(null)
  const [makeOwnerReason, setMakeOwnerReason] = useState('')
  const [makeOwnerOldOwnerId, setMakeOwnerOldOwnerId] = useState<
    string | undefined
  >(undefined)
  const makeOwnerTargetIdRef = useRef<string | undefined>(undefined)
  const [selectedRoleId, setSelectedRoleId] = useState('')
  const [isAddMembersOpen, setIsAddMembersOpen] = useState(false)
  const [addMembersDialogKey, setAddMembersDialogKey] = useState(0)

  const canAddMembers = useCanCreateAccount()
  const orgFeatures = useGetOrganisationFeatures(
    { pathParams: { orgId } },
    { enabled: canAddMembers },
  )
  const isOrgSsoEnabled = orgFeatures.data?.sso?.enabled === true
  const { userRoles, hasAdminRights, hasOperatorRights } = useRBACPermissions()
  const rolesResolved = userRoles.isSuccess || userRoles.isError
  const canTransferOwnership = hasAdminRights() || hasOperatorRights()
  const showActionsColumn =
    canManageSupportUsers || canEditRoles || canTransferOwnership

  useEffect(() => {
    if (!addMembersRequested || !rolesResolved) {
      return
    }
    if (canAddMembers && orgFeatures.isLoading) {
      return
    }
    if (canAddMembers && !isOrgSsoEnabled) {
      setIsAddMembersOpen(true)
    }
    onAddMembersRequestHandled()
  }, [
    addMembersRequested,
    rolesResolved,
    canAddMembers,
    orgFeatures.isLoading,
    isOrgSsoEnabled,
    onAddMembersRequestHandled,
  ])

  const users = useGetOrgUsers({
    pathParams: { orgId },
  })

  const orgRoles = useGetOrgRoles(
    { pathParams: { orgId } },
    { enabled: canEditRoles },
  )

  const addSupportUser = useAddSupportUser({
    onSuccess: () => {
      showOrUpdateToast(ADD_SUPPORT_USER_TOAST_ID, {
        ...SUCCESS_TOAST_OPTION,
        title: String(t('orgDetails.members.toast.addSuccess')),
      })
      users.refetch()
      setIsAddDialogOpen(false)
      setReason('')
    },
    onError: (error) => {
      showOrUpdateToast(ADD_SUPPORT_USER_TOAST_ID, {
        ...ERROR_TOAST_OPTION,
        title: String(t('orgDetails.members.toast.addError')),
        description: get(error, 'message'),
      })
    },
  })

  const removeMember = useRemoveSupportUser({
    onSuccess: () => {
      showOrUpdateToast(REMOVE_SUPPORT_USER_TOAST_ID, {
        ...SUCCESS_TOAST_OPTION,
        title: String(t('orgDetails.members.toast.removeSuccess')),
      })
      users.refetch()
      setIsRemoveDialogOpen(false)
      setSelectedUser(null)
    },
    onError: (error) => {
      showOrUpdateToast(REMOVE_SUPPORT_USER_TOAST_ID, {
        ...ERROR_TOAST_OPTION,
        title: String(t('orgDetails.members.toast.removeError')),
        description: get(error, 'message'),
      })
    },
  })

  const replaceUserRoles = useReplaceUserRoles({
    onSuccess: () => {
      showOrUpdateToast(EDIT_ROLE_TOAST_ID, {
        ...SUCCESS_TOAST_OPTION,
        title: String(t('orgDetails.members.toast.editRoleSuccess')),
      })
      users.refetch()
      closeEditRoleDialog()
    },
    onError: (error) => {
      showOrUpdateToast(EDIT_ROLE_TOAST_ID, {
        ...ERROR_TOAST_OPTION,
        title: String(t('orgDetails.members.toast.editRoleError')),
        description: get(error, 'message'),
      })
    },
  })

  const changeOrgOwner = useChangeOrgOwner({
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['api', 'v2', 'orgs', orgId],
          exact: true,
        }),
        invalidateOrgUsers(queryClient, orgId),
      ]),
    onSuccess: (_data, variables) => {
      showOrUpdateToast(MAKE_OWNER_TOAST_ID, {
        ...SUCCESS_TOAST_OPTION,
        title: String(t('orgDetails.members.toast.makeOwnerSuccess')),
      })
      if (isCurrentMakeOwnerTarget(variables)) {
        closeMakeOwnerDialog()
      }
    },
    onError: (error, variables) => {
      const failure = makeOwnerFailure(error)
      showOrUpdateToast(MAKE_OWNER_TOAST_ID, {
        ...ERROR_TOAST_OPTION,
        title: String(t('orgDetails.members.toast.makeOwnerError')),
        description: makeOwnerErrorDescription(error, failure),
      })
      if (failure?.closesDialog && isCurrentMakeOwnerTarget(variables)) {
        closeMakeOwnerDialog()
      }
    },
  })

  const availableRoles = useMemo(
    () => orgRoles.data?.roles ?? [],
    [orgRoles.data],
  )

  function handleAddMyself(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!reason.trim()) {
      return
    }

    addSupportUser.mutate({
      pathParams: { orgId },
      body: {
        userId: 'me',
        reason: reason.trim(),
      },
    })
  }

  function handleRemoveUser() {
    if (!selectedUser) {
      return
    }

    removeMember.mutate({
      pathParams: {
        orgId,
        userId: selectedUser.id,
      },
    })
  }

  function openRemoveDialog(user: OrgUser) {
    setSelectedUser(user)
    setIsRemoveDialogOpen(true)
  }

  function openEditRoleDialog(user: OrgUser) {
    setSelectedUser(user)
    const currentRole = availableRoles.find((role) =>
      user.roles?.includes(role.name),
    )
    setSelectedRoleId(currentRole ? String(currentRole.id) : '')
    setReason('')
    setIsEditRoleDialogOpen(true)
  }

  function closeAddDialog() {
    setIsAddDialogOpen(false)
    setReason('')
  }

  function closeEditRoleDialog() {
    setIsEditRoleDialogOpen(false)
    setSelectedUser(null)
    setSelectedRoleId('')
    setReason('')
  }

  function makeOwnerErrorDescription(
    error: unknown,
    failure: MakeOwnerFailure | undefined,
  ): string | undefined {
    const backendText = badRequestMessage(error)
    if (backendText) {
      return backendText
    }
    return failure ? String(t(failure.errorKey)) : get(error, 'message')
  }

  function canMakeOwner(user: OrgUser) {
    return (
      canTransferOwnership &&
      Boolean(ownerId) &&
      user.id !== ownerId &&
      !user.roles.includes(SUPPORT_ADMIN_ROLE)
    )
  }

  function openMakeOwnerDialog(user: OrgUser) {
    makeOwnerTargetIdRef.current = user.id
    setMakeOwnerTarget(user)
    setMakeOwnerOldOwnerId(ownerId)
    setMakeOwnerReason('')
    setIsMakeOwnerDialogOpen(true)
  }

  function closeMakeOwnerDialog() {
    makeOwnerTargetIdRef.current = undefined
    setIsMakeOwnerDialogOpen(false)
    setMakeOwnerTarget(null)
    setMakeOwnerOldOwnerId(undefined)
    setMakeOwnerReason('')
  }

  function isCurrentMakeOwnerTarget(variables: ChangeOrgOwnerVariables) {
    return variables.body.new_user_id === makeOwnerTargetIdRef.current
  }

  function handleMakeOwner(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const trimmedReason = makeOwnerReason.trim()
    if (!makeOwnerTarget || !makeOwnerOldOwnerId || !trimmedReason) {
      return
    }

    changeOrgOwner.mutate({
      pathParams: { orgId },
      body: {
        old_user_id: makeOwnerOldOwnerId,
        new_user_id: makeOwnerTarget.id,
        reason: trimmedReason,
      },
    })
  }

  function handleEditRole(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!selectedUser || !selectedRoleId || !reason.trim()) {
      return
    }

    replaceUserRoles.mutate({
      pathParams: { orgId, userId: selectedUser.id },
      body: {
        roleIds: [Number(selectedRoleId)],
        reason: reason.trim(),
      },
    })
  }

  return (
    <>
      <SectionCard
        title={t('orgDetails.members.title')}
        actions={
          canAddMembers || canManageSupportUsers ? (
            <HStack gap={2}>
              {canAddMembers && (
                <DisabledReasonTooltip
                  reason={
                    isOrgSsoEnabled
                      ? String(t('orgDetails.members.addMembers.ssoEnabled'))
                      : undefined
                  }
                >
                  <Button
                    size="xs"
                    colorPalette="brand"
                    variant="outline"
                    disabled={orgFeatures.isLoading || isOrgSsoEnabled}
                    onClick={() => setIsAddMembersOpen(true)}
                  >
                    <Icon as={UserPlus} />
                    {t('orgDetails.members.addMembers.button')}
                  </Button>
                </DisabledReasonTooltip>
              )}
              {canManageSupportUsers && (
                <Button
                  size="xs"
                  colorPalette="brand"
                  onClick={() => setIsAddDialogOpen(true)}
                >
                  <Icon as={Plus} />
                  {t('orgDetails.members.addButton')}
                </Button>
              )}
            </HStack>
          ) : undefined
        }
      >
        {users.isError ? (
          <ApiErrorState
            apiResource={users as unknown as UseQueryResult}
            errorTitle={t('orgDetails.members.error.title')}
            errorDescription={t('orgDetails.members.error.description')}
          />
        ) : (
          <Table.Root variant="line" colorPalette="gray">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  {t('orgDetails.members.email')}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t('orgDetails.members.id')}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t('orgDetails.members.roles')}
                </Table.ColumnHeader>
                {showActionsColumn && <Table.ColumnHeader width="96px" />}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {users.isLoading && (
                <SkeletonTable
                  id="members-skeleton"
                  skeletonWidths={
                    showActionsColumn ? [180, 200, 120, 40] : [180, 200, 120]
                  }
                  rows={3}
                />
              )}
              {users.data?.map((user) => (
                <Table.Row key={`user-${user.id}`}>
                  <Table.Cell>
                    <CopyableValue
                      value={user.email}
                      onClick={() =>
                        routeToAccountManagementWithPrefilledEmail(
                          navigate,
                          user.email,
                        )
                      }
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <CopyableValue
                      value={user.id}
                      externalLink={
                        user.id ? getAuth0UserLink(user.id) : undefined
                      }
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <HStack gap={1} flexWrap="wrap">
                      {user.roles?.length ? (
                        user.roles.map((role) => (
                          <MemberRoleBadge key={role} roleName={role} />
                        ))
                      ) : (
                        <Text fontSize="sm">
                          {tCommon('general.notAvailable')}
                        </Text>
                      )}
                    </HStack>
                  </Table.Cell>
                  {showActionsColumn && (
                    <Table.Cell>
                      <HStack gap={1} justifyContent="flex-end">
                        {canEditRoles && (
                          <IconButton
                            aria-label={String(
                              t('orgDetails.members.editRoleButton'),
                            )}
                            size="xs"
                            variant="ghost"
                            disabled={orgRoles.isLoading}
                            onClick={() => openEditRoleDialog(user)}
                          >
                            <Icon as={Pencil} />
                          </IconButton>
                        )}
                        {canMakeOwner(user) && (
                          <IconButton
                            aria-label={String(
                              t('orgDetails.members.makeOwnerButton'),
                            )}
                            size="xs"
                            variant="ghost"
                            onClick={() => openMakeOwnerDialog(user)}
                          >
                            <Icon as={Crown} />
                          </IconButton>
                        )}
                        {canManageSupportUsers &&
                          user.roles.includes(SUPPORT_ADMIN_ROLE) && (
                            <IconButton
                              aria-label={String(
                                t('orgDetails.members.removeButton'),
                              )}
                              size="xs"
                              variant="ghost"
                              colorPalette="red"
                              onClick={() => openRemoveDialog(user)}
                            >
                              <Icon as={Trash2} />
                            </IconButton>
                          )}
                      </HStack>
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </SectionCard>

      <FormDialog
        open={isAddDialogOpen}
        onClose={closeAddDialog}
        onSubmit={handleAddMyself}
        title={t('orgDetails.members.addDialog.title')}
        cancelLabel={t('organization.features.actions.cancel')}
        submitLabel={t('orgDetails.members.addDialog.submitButton')}
        submitColorPalette="brand"
        submitLoading={addSupportUser.isPending}
        submitDisabled={!reason.trim() || addSupportUser.isPending}
      >
        <Text fontSize="sm" mb={4}>
          {t('orgDetails.members.addDialog.description')}
        </Text>
        <ReasonField
          label={t('orgDetails.members.addDialog.reasonLabel')}
          placeholder={String(
            t('orgDetails.members.addDialog.reasonPlaceholder'),
          )}
          helper={t('orgDetails.members.addDialog.reasonHelper')}
          value={reason}
          onChange={setReason}
        />
      </FormDialog>

      <Dialog.Root
        open={isRemoveDialogOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            setIsRemoveDialogOpen(false)
            setSelectedUser(null)
          }
        }}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header pb={3}>
              <Dialog.Title>
                {t('orgDetails.members.removeDialog.title')}
              </Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body>
              <Text fontSize="sm">
                {t('orgDetails.members.removeDialog.message', {
                  email: selectedUser?.email,
                })}
              </Text>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsRemoveDialogOpen(false)
                    setSelectedUser(null)
                  }}
                >
                  {t('organization.features.actions.cancel')}
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleRemoveUser}
                  loading={removeMember.isPending}
                  disabled={removeMember.isPending}
                >
                  {t('orgDetails.members.removeDialog.confirmButton')}
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      <FormDialog
        open={isEditRoleDialogOpen}
        onClose={closeEditRoleDialog}
        onSubmit={handleEditRole}
        title={t('orgDetails.members.editRoleDialog.title')}
        cancelLabel={t('organization.features.actions.cancel')}
        submitLabel={t('orgDetails.members.editRoleDialog.submitButton')}
        submitColorPalette="brand"
        submitLoading={replaceUserRoles.isPending}
        submitDisabled={
          !selectedRoleId || !reason.trim() || replaceUserRoles.isPending
        }
      >
        <Text fontSize="sm" mb={4}>
          {t('orgDetails.members.editRoleDialog.description', {
            email: selectedUser?.email,
          })}
        </Text>
        <Field.Root required mb={4}>
          <Field.Label>
            {t('orgDetails.members.editRoleDialog.roleLabel')}
          </Field.Label>
          <NativeSelect.Root disabled={orgRoles.isLoading}>
            <NativeSelect.Field
              value={selectedRoleId}
              onChange={(event) => setSelectedRoleId(event.target.value)}
            >
              <option value="" disabled>
                {String(t('orgDetails.members.editRoleDialog.rolePlaceholder'))}
              </option>
              {availableRoles.map((role) => (
                <option key={role.id} value={String(role.id)}>
                  {role.name}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          <Field.HelperText>
            {t('orgDetails.members.editRoleDialog.roleHelper')}
          </Field.HelperText>
        </Field.Root>
        <ReasonField
          label={t('orgDetails.members.editRoleDialog.reasonLabel')}
          placeholder={String(
            t('orgDetails.members.editRoleDialog.reasonPlaceholder'),
          )}
          helper={t('orgDetails.members.editRoleDialog.reasonHelper')}
          value={reason}
          onChange={setReason}
        />
      </FormDialog>

      <FormDialog
        open={isMakeOwnerDialogOpen}
        onClose={closeMakeOwnerDialog}
        onSubmit={handleMakeOwner}
        title={t('orgDetails.members.makeOwnerDialog.title')}
        cancelLabel={t('organization.features.actions.cancel')}
        submitLabel={t('orgDetails.members.makeOwnerDialog.submitButton')}
        submitColorPalette="red"
        submitLoading={changeOrgOwner.isPending}
        submitDisabled={!makeOwnerReason.trim() || changeOrgOwner.isPending}
      >
        <Text fontSize="sm" mb={4} overflowWrap="anywhere">
          {t('orgDetails.members.makeOwnerDialog.description', {
            email: makeOwnerTarget?.email,
          })}
        </Text>
        <List.Root fontSize="sm" mb={4} ps={5} overflowWrap="anywhere">
          <List.Item>
            {t('orgDetails.members.makeOwnerDialog.consequences.currentOwner')}
          </List.Item>
          <List.Item>
            {t('orgDetails.members.makeOwnerDialog.consequences.newOwner', {
              email: makeOwnerTarget?.email,
            })}
          </List.Item>
          <List.Item>
            {t('orgDetails.members.makeOwnerDialog.consequences.billing')}
          </List.Item>
          <List.Item>
            {t('orgDetails.members.makeOwnerDialog.consequences.restart')}
          </List.Item>
        </List.Root>
        <ReasonField
          label={t('orgDetails.members.makeOwnerDialog.reasonLabel')}
          placeholder={String(
            t('orgDetails.members.makeOwnerDialog.reasonPlaceholder'),
          )}
          helper={t('orgDetails.members.makeOwnerDialog.reasonHelper')}
          value={makeOwnerReason}
          onChange={setMakeOwnerReason}
        />
      </FormDialog>

      {canAddMembers && (
        <AddMembersDialog
          key={addMembersDialogKey}
          open={isAddMembersOpen}
          orgId={orgId}
          canonicalOrgId={canonicalOrgId}
          onClose={() => setIsAddMembersOpen(false)}
          onExitComplete={() => setAddMembersDialogKey((key) => key + 1)}
        />
      )}
    </>
  )
}
